import { useEffect, useMemo, useRef, useState } from 'react'
import LiFiClient from '../../clients/LiFiClient'
import { EarnChain, EarnProtocol, EarnVault, ListVaultsParams } from '../../interfaces/earn'
import { logger } from '../../logger'
import { useUserEVMChains } from '../../state'

const PAGE_SIZE = 50

type Filters = Omit<ListVaultsParams, 'cursor' | 'limit'>

export const useEarnVaults = (filters: Filters) => {
  const lifi = useMemo(() => new LiFiClient(), [])
  const { userEVMChains } = useUserEVMChains()

  const [chains, setChains] = useState<EarnChain[]>([])
  const [chainsLoading, setChainsLoading] = useState(true)

  const [protocols, setProtocols] = useState<EarnProtocol[]>([])
  const [protocolsLoading, setProtocolsLoading] = useState(true)

  const [vaults, setVaults] = useState<EarnVault[]>([])
  const [vaultsTotal, setVaultsTotal] = useState(0)
  const [vaultsLoading, setVaultsLoading] = useState(false)

  const fetchInfo = useRef<{ cursor?: string; isFetching: boolean; hasReachedEnd: boolean }>({
    cursor: undefined,
    isFetching: false,
    hasReachedEnd: false,
  })

  useEffect(() => {
    lifi
      .listChains()
      .then(setChains)
      .catch((e) => logger.error('Failed to fetch earn chains', { originalError: e }))
      .finally(() => setChainsLoading(false))

    lifi
      .listProtocols()
      .then(setProtocols)
      .catch((e) => logger.error('Failed to fetch earn protocols', { originalError: e }))
      .finally(() => setProtocolsLoading(false))
  }, [])

  const supportedChains = useMemo(
    () => chains.filter((c) => userEVMChains.includes(c.chainId)),
    [chains, userEVMChains]
  )

  const filtersKey = JSON.stringify(filters)
  const supportedChainsKey = userEVMChains.join(',')

  const fetchPage = async (cursor?: string) => {
    fetchInfo.current = { cursor, isFetching: true, hasReachedEnd: false }
    setVaultsLoading(true)

    try {
      const response = await lifi.listVaults({
        ...filters,
        limit: PAGE_SIZE,
        cursor,
      })

      fetchInfo.current = {
        cursor: response.nextCursor,
        isFetching: false,
        hasReachedEnd: !response.nextCursor || response.data.length === 0,
      }

      const data = filters.chainId
        ? response.data
        : response.data.filter((v) => userEVMChains.includes(v.chainId))

      return { ...response, data }
    } catch (e) {
      logger.error('Failed to fetch earn vaults', { originalError: e })
      fetchInfo.current = { cursor, isFetching: false, hasReachedEnd: true }
      return { data: [] as EarnVault[], nextCursor: undefined, total: 0 }
    } finally {
      setVaultsLoading(false)
    }
  }

  const reload = async () => {
    fetchInfo.current = { cursor: undefined, isFetching: false, hasReachedEnd: false }
    const response = await fetchPage(undefined)
    setVaults(response.data)
    setVaultsTotal(response.total)
  }

  const fetchNextPage = async () => {
    if (fetchInfo.current.isFetching || fetchInfo.current.hasReachedEnd) return

    const response = await fetchPage(fetchInfo.current.cursor)
    setVaults((prev) => [...prev, ...response.data])
  }

  useEffect(() => {
    reload()
  }, [filtersKey, supportedChainsKey])

  return {
    chains: supportedChains,
    chainsLoading,
    protocols,
    protocolsLoading,
    vaults,
    vaultsTotal,
    vaultsLoading,
    fetchNextPage,
    reload,
  }
}
